/**
 * Scalekit authentication service for frontend.
 *
 * Handles authentication via Scalekit backend endpoints.
 * Uses cookie-based sessions (HttpOnly cookies set by backend).
 */

export interface ScalekitUser {
  user_id: string
  organization_id: string | null
  email: string | null
  name: string | null
  claims: Record<string, unknown> | null
}

export interface Organization {
  id: string
  name: string
  slug: string
}

export interface LoginOptions {
  organization_id?: string
  connection_id?: string
  login_hint?: string
  redirect_url?: string
}

interface LoginResponse {
  authorization_url: string
}

interface RefreshResponse {
  access_token: string
  refresh_token: string | null
  expires_in: number | null
}

interface LogoutResponse {
  success: boolean
  logout_url: string | null
}

interface PasswordlessSendResponse {
  success: boolean
  message: string
  auth_request_id: string | null
  expires_in: number | null
}

interface PasswordlessVerifyResponse {
  success: boolean
  user_id: string | null
  organization_id: string | null
  email: string | null
  name: string | null
  access_token: string | null
  expires_in: number | null
}

class ScalekitAuthService {
  private readonly apiBaseUrl: string

  // Single-flight refresh: when N callers (apiClient retries, AuthContext
  // periodic check, AuthContext init fallback) hit a 401 in parallel, they
  // all await the same in-flight refresh. Critical with refresh token
  // rotation — parallel refreshes would burn each other's rotated tokens
  // and cause spurious logouts.
  private refreshInFlight: Promise<boolean> | null = null

  constructor() {
    // Use empty string to leverage Vite proxy for /auth/* routes
    // This ensures cookies are same-origin and properly handled
    this.apiBaseUrl = ''
  }

  /**
   * Initiate login flow by redirecting to Scalekit hosted login.
   */
  async initiateLogin(options: LoginOptions = {}): Promise<void> {
    const params = new URLSearchParams()

    if (options.organization_id) {
      params.set('organization_id', options.organization_id)
    }
    if (options.connection_id) {
      params.set('connection_id', options.connection_id)
    }
    if (options.login_hint) {
      params.set('login_hint', options.login_hint)
    }
    if (options.redirect_url) {
      params.set('redirect_url', options.redirect_url)
    }

    const queryString = params.toString()
    const url = `${this.apiBaseUrl}/auth/login${queryString ? `?${queryString}` : ''}`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to initiate login')
    }

    const data: LoginResponse = await response.json()
    window.location.href = data.authorization_url
  }

  /**
   * Get current authenticated user.
   * Returns null if not authenticated.
   */
  async getCurrentUser(): Promise<ScalekitUser | null> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.status === 401) {
        return null
      }

      if (!response.ok) {
        console.warn('Failed to get current user:', response.status)
        return null
      }

      return await response.json()
    } catch (error) {
      console.warn('Error getting current user:', error)
      return null
    }
  }

  /**
   * Refresh the session token.
   * Returns true if refresh succeeded.
   * Coalesces concurrent calls — see refreshInFlight comment above.
   */
  async refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight
    this.refreshInFlight = this._doRefresh().finally(() => {
      this.refreshInFlight = null
    })
    return this.refreshInFlight
  }

  private async _doRefresh(): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        console.warn('Session refresh failed:', response.status)
        return false
      }

      const _data: RefreshResponse = await response.json()
      return true
    } catch (error) {
      console.warn('Error refreshing session:', error)
      return false
    }
  }

  /**
   * Logout the current user.
   * Optionally redirects to Scalekit logout URL.
   * @returns true if redirect was initiated (caller should not do further navigation)
   */
  async logout(redirectToProvider: boolean = false): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok && redirectToProvider) {
        const data: LogoutResponse = await response.json()
        if (data.logout_url) {
          window.location.href = data.logout_url
          // Return true - caller should not navigate further
          return true
        }
      }
      return false
    } catch (error) {
      console.warn('Error during logout:', error)
      return false
    }
  }

  /**
   * Send a passwordless OTP code to the user's email.
   * Returns auth_request_id needed for verification.
   */
  async sendPasswordlessCode(email: string): Promise<PasswordlessSendResponse> {
    const response = await fetch(`${this.apiBaseUrl}/auth/passwordless/send`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Failed to send verification code')
    }

    return await response.json()
  }

  /**
   * Verify the OTP code and authenticate the user.
   * Sets session cookies on success.
   */
  async verifyPasswordlessCode(
    authRequestId: string,
    code: string
  ): Promise<PasswordlessVerifyResponse> {
    const response = await fetch(`${this.apiBaseUrl}/auth/passwordless/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        auth_request_id: authRequestId,
        code: code,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Invalid verification code')
    }

    return await response.json()
  }

  /**
   * List organizations the current user belongs to.
   * Note: This endpoint is not implemented yet - returns empty array.
   */
  async listOrganizations(): Promise<Organization[]> {
    // TODO: Implement when auth service has /api/users/me/organizations endpoint
    // For now, return empty array to avoid console errors
    return []
  }

  /**
   * Switch to a different organization.
   */
  async switchOrganization(organizationId: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/users/me/organizations/${organizationId}/switch`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        console.warn('Failed to switch organization:', response.status)
        return false
      }

      return true
    } catch (error) {
      console.warn('Error switching organization:', error)
      return false
    }
  }
}

export const scalekitAuthService = new ScalekitAuthService()
export default scalekitAuthService
